/* eslint-disable i18next/no-literal-string */
import { cn } from "~/lib/utils";
import { LandingPageHeaderH1 } from "~/components/landing/LandingText";
import TuroLogo from "~/components/turo/TuroLogo";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const turoHeroBackground = imageUrl("/images/turo/turo-hero-background.jpg");

/**
 * Ported from bouncieweb's `LandingPageComponents/TuroHero.js`.
 *
 * A rounded hero card with a parking-lot photo, the Turo wordmark, and a
 * left-aligned headline. The `#7b8281` fill behind the image is the photo's
 * average color — it smooths over the load delay, so it is preserved.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 */

const heroTextShadow = { textShadow: "0 0 40px #000" };

export default function TuroHero() {
  return (
    // HeroWrapper
    <div className="mt-[26px] mb-12 flex justify-center overflow-hidden sm:mx-[50px] sm:mt-8 md:mb-20">
      {/* HeroRectangle */}
      <div
        className={cn(
          "relative h-[454px] w-full overflow-hidden text-white",
          "bg-[#7b8281]",
          "sm:h-[400px] sm:rounded-[20px] xl:max-w-[1400px]",
        )}
      >
        {/* HeroBgImage */}
        <Picture
          src={turoHeroBackground}
          loading="eager"
          alt="Person holding a car key with a blurred parking lot full of cars in the background"
          className={cn(
            "absolute h-full w-full max-w-none object-cover",
            "object-[center_center] min-[431px]:object-[bottom_center]",
            "sm:block sm:object-[60%_center] md:object-[60%_0%] lg:object-center",
          )}
        />

        {/* Dimmer — mobile-only scrim */}
        <div className="absolute top-0 left-0 h-full w-full bg-black/[0.21] md:hidden" />

        {/* ContentArea */}
        <div className="relative mt-8 mr-[34px] ml-[34px] md:mt-[42px] md:mr-[85px] md:ml-[85px] lg:mt-[52px] lg:mr-[140px] lg:ml-[140px]">
          <TuroLogo />

          {/* HeroHeader (LandingPageHeaderH1 + hero overrides) */}
          <LandingPageHeaderH1
            className={cn(
              "mt-[23px] mb-6 max-w-[550px] text-left [text-wrap:wrap]",
              "text-[40px] leading-[48px]",
              "min-[431px]:text-[40px] sm:mt-[30px] sm:text-[48px] sm:leading-[56px]",
            )}
            style={heroTextShadow}
          >
            Safeguard your Turo vehicle with Bouncie!
          </LandingPageHeaderH1>

          {/* HeroSubHeader */}
          <div className="text-[20px] sm:text-[24px]" style={heroTextShadow}>
            Ensure vehicle trust and safety.
          </div>
        </div>
      </div>
    </div>
  );
}
